"use client";

import type { OAuthProvider } from "@workspace/auth-shared/types";

/**
 * Start a standard OAuth flow by navigating to the API, which redirects to the
 * provider and — after the provider comes back to the API's callback — on to
 * `returnTo` with the session already set. No page of ours handles the code.
 *
 * Failures come back to the auth FE page for the flow (/login/oauth, the
 * connected-accounts settings, or /connect/oauth/callback) as `oauth_error` +
 * `oauth_message` query params; see `oauthErrorFrom`.
 *
 * Not for `atproto` — Bluesky needs a handle first and has its own endpoints.
 */
export function startOAuth(
  provider: OAuthProvider,
  options: {
    intent?: "login" | "link";
    /** A path on this FE or a URL on an owned origin; the API rejects others. */
    returnTo?: string | null;
    /** Delegation scope names — vault mode. */
    scopes?: string[];
    /** Vault mode: client id of the first-party service to grant on success. */
    agent?: string;
  } = {},
): void {
  const params = new URLSearchParams();
  if (options.intent) params.set("intent", options.intent);
  if (options.returnTo) params.set("return_to", options.returnTo);
  if (options.scopes?.length) params.set("scopes", options.scopes.join(","));
  if (options.agent) params.set("agent", options.agent);
  const query = params.toString();
  window.location.assign(
    `${process.env.NEXT_PUBLIC_API_URL}/v1/oauth/${provider}/start${query ? `?${query}` : ""}`,
  );
}

/** The failure a flow was redirected back with, if any. */
export function oauthErrorFrom(searchParams: URLSearchParams): string | null {
  if (!searchParams.get("oauth_error")) return null;
  return searchParams.get("oauth_message") || "Sign-in failed. Please try again.";
}
